import { Injectable } from '@angular/core';
import { TableSelectionHandler } from '@cbre/components';
import { MatTableDataSource } from '@angular/material/table';
import { SelectionModel } from '@angular/cdk/collections';
import { GetRecordIdsPipe } from '@miq/domain/property-sets';
import { EntityType } from '@miq/domain';
import { Sort } from '@angular/material/sort';
import { COLUMNS_TO_SHOW } from './property-set-records-table.config';
import { PropertySetsHttpService } from '../../services/http/property-sets-http.service';
import { filter } from 'rxjs';
import { PageChange, SortingSelection } from '../../common/sorting.model';

export interface SelectedItem {
  id: number;
  isActive: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class PropertySetRecordsSelectionHandleService<T> implements TableSelectionHandler<T> {
  public dataSource: MatTableDataSource<Record<string, any>> = new MatTableDataSource([]);
  public selection: SelectionModel<T> = new SelectionModel(true, []);
  public deletedRecords: number[] = [];
  public entity: EntityType;
  disableRowTooltip?: string;
  selectedIds: number[] = [];
  public totalRecordCount: number;
  public sortingSelection: SortingSelection;
  public propertySetId: number;
  public propertySetFingerPrint: string;
  public searchTextInput: string;

  defaultPageInfo: PageChange = { pageSize: 20, pageIndex: 0 };
  public pageChange: PageChange = this.defaultPageInfo;

  #columnDataTypeByFieldMap = new Map();

  constructor(private propertySetsHttpService: PropertySetsHttpService) {
    COLUMNS_TO_SHOW.forEach(col => {
      this.#columnDataTypeByFieldMap.set(col.field, col.dataType);
    });
  }

  configure(
    dataSource: MatTableDataSource<Record<string, any>>,
    selection: SelectionModel<T>
  ): TableSelectionHandler<T> {
    this.dataSource = dataSource;
    this.selection = selection;
    this.disableRowTooltip = 'This record is pending removal by EDP';
    return this;
  }

  isAllSelected(): boolean {
    const selectedItems = this.selection.selected;
    const dataItems = this.dataSource.data;
    return selectedItems?.length === dataItems?.length;
  }

  /**
   * Select all the items in the selected page
   */
  selectAllToggle(): void {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.dataSource.data.forEach(row => {
        this.selection.select(row as T);
      });
    }
  }

  checkboxLabel(row: T): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    const indexOfRow = this.dataSource.data.indexOf(row);
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${indexOfRow + 1}`;
  }

  /**
   * Select or Unselect a row
   * @param row property record
   */
  selectionToggle(row: T): void {
    this.selection.toggle(row);
  }

  clearSelection(): void {
    this.selection.clear();
  }

  isSelectionDisabled = (row: T) => {
    if (this.deletedRecords?.length === 0) return false;
    const [Id] = new GetRecordIdsPipe().transform([row], this.entity);
    return (this.deletedRecords || []).includes(Id);
  };

  get selectedItems() {
    return this.selection.selected;
  }

  onPageChange(page: PageChange) {
    this.pageChange = page;
    this.loadProperties();
  }

  onSortChange(sortData: Sort) {
    this.sortingSelection =
      !sortData || !sortData.direction
        ? null
        : {
            field: sortData.active,
            order: sortData.direction,
            dataType: this.#columnDataTypeByFieldMap.get(sortData.active),
          };
    this.loadProperties();
  }

  getPayloadFormatted() {
    const multiSort = this.sortingSelection ? { multiSort: [this.sortingSelection] } : {};
    const offset = this.pageChange.pageIndex * this.pageChange.pageSize;
    return {
      entity: 'property',
      filters: [
        {
          type: 'multifield',
          typeAhead: true,
          propertySetTypeAhead: true,
          values: this.searchTextInput, // search text
        },
        {
          type: 'equal',
          field: 'usages/record_set/set_id',
          key: this.propertySetId,
          dataType: 'text',
          name: 'Property Set',
          values: this.propertySetFingerPrint, // fingerprint
        },
        {
          key: 16933,
          type: 'equal',
          field: 'miq_record_hide_f',
          dataType: 'boolean',
          name: 'Hidden Properties',
          values: false,
        },
      ],
      limit: this.pageChange.pageSize,
      offset,
      ...multiSort,
    };
  }

  loadProperties() {
    if (this.propertySetId === -1) return;
    const request = this.getPayloadFormatted();
    const getPropertySetProperties$ = this.propertySetsHttpService.getPropertySetProperties(request);
    if (getPropertySetProperties$) {
      getPropertySetProperties$.pipe(filter(Boolean)).subscribe(response => {
        this.selectedIds = this.selection.selected.map((item: Record<string, any>) => item?.entity?.id);
        this.clearSelection();
        this.dataSource.data = response.results;
        this.totalRecordCount = response.totalCount;
        this.restoreSelection();
      });
    }
  }

  restoreSelection() {
    this.dataSource.data.forEach((row: Record<string, any>) => {
      if (this.selectedIds.includes(row?.entity?.id)) {
        this.selection.select(row as unknown as T);
      }
    });
  }

  resetValues() {
    this.pageChange = this.defaultPageInfo;
    this.sortingSelection = null;
    this.totalRecordCount = null;
  }
}
